import { useMutation, useQueryClient, QueryClient } from "@tanstack/react-query";
import { pppService } from "@/services/pppService";
import { IUpsertPpp, PppReorderItem } from "@/types";

/** Deliverable order/candidacy derives from PPPs, so rank or link changes ripple to every list. */
const invalidateDerived = (qc: QueryClient) => {
  for (const key of [["ppps"], ["priorities"], ["my-onething"], ["team-priorities"]]) {
    qc.invalidateQueries({ queryKey: key });
  }
};

export const useCreatePpp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IUpsertPpp) => pppService.createPpp(data),
    // A new PPP links KRs, so the OKR tree gains a child too.
    onSuccess: () => { invalidateDerived(qc); invalidateOkr(qc); },
  });
};

/** KR and objective rows show the PPPs linked under them. */
const invalidateOkr = (qc: QueryClient) => {
  for (const key of [["key-results"], ["objectives"]]) {
    qc.invalidateQueries({ queryKey: key });
  }
};

export const useUpdatePpp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pid, data }: { pid: string; data: Partial<IUpsertPpp> }) => pppService.updatePpp(pid, data),
    // Deliverables embed the PPP name/type, so a rename must refresh them too.
    onSuccess: () => invalidateDerived(qc),
  });
};

export const useDeletePpp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pid: string) => pppService.deletePpp(pid),
    onSuccess: () => invalidateDerived(qc),
  });
};

export const useReorderPpps = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: PppReorderItem[]) => pppService.reorderPpps(items),
    onSettled: () => invalidateDerived(qc),
  });
};
